import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, Clock, Loader2 } from "lucide-react";
import { Reveal, SectionHeader } from "./Shared";
import { WHATSAPP_URL, WHATSAPP_NUMBER_DISPLAY, API } from "@/lib/site";

const INITIAL = { name: "", email: "", phone: "", capital_tier: "RM10,000+", markets: "Bursa Malaysia", message: "" };

const inputCls =
  "w-full rounded-lg border border-line bg-obsidian px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-alpha/60";

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email and phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry received — our senior desk will reach out within 24 hours.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Could not send your enquiry. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <SectionHeader
            eyebrow="Get Started"
            title="Claim your institutional trading advantage today."
            sub="Speak directly with our senior desk analyst on WhatsApp, or send a priority onboarding enquiry and we will call you back."
          />

          <Reveal delay={0.15}>
            <a
              data-testid="contact-whatsapp-button"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="glow-alpha mt-10 inline-flex items-center gap-3 rounded-full bg-alpha px-8 py-4 text-base font-bold text-obsidian transition-transform duration-300 hover:scale-[1.04]"
            >
              <MessageCircle size={20} /> Chat on WhatsApp — {WHATSAPP_NUMBER_DISPLAY}
            </a>
            <div className="mt-8 space-y-3 font-mono text-xs text-slate-500">
              <p className="flex items-center gap-2">
                <Clock size={14} className="text-gold" /> Desk hours: 9:00 AM — 9:00 PM MYT, Monday to Friday
              </p>
              <p className="flex items-center gap-2">
                <span className="h-2 w-2 animate-pulse rounded-full bg-alpha" /> Average first response: under 15 minutes during desk hours
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="rounded-2xl border border-line bg-surface p-6 md:p-8"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Priority Onboarding Form</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <input data-testid="contact-name-input" className={inputCls} placeholder="Full Name *" value={form.name} onChange={set("name")} />
              <input data-testid="contact-email-input" type="email" className={inputCls} placeholder="Email Address *" value={form.email} onChange={set("email")} />
              <input data-testid="contact-phone-input" className={inputCls} placeholder="Phone (MY / SG) *" value={form.phone} onChange={set("phone")} />
              <select data-testid="contact-capital-select" className={inputCls} value={form.capital_tier} onChange={set("capital_tier")}>
                <option>RM10,000+</option>
                <option>RM30,000+</option>
                <option>RM100,000+</option>
              </select>
              <select data-testid="contact-markets-select" className={`${inputCls} sm:col-span-2`} value={form.markets} onChange={set("markets")}>
                <option>Bursa Malaysia</option>
                <option>Forex &amp; Comex</option>
                <option>SGX &amp; Nasdaq</option>
                <option>All Markets</option>
              </select>
              <textarea
                data-testid="contact-message-input"
                rows={4}
                className={`${inputCls} resize-none sm:col-span-2`}
                placeholder="Tell us about your trading goals..."
                value={form.message}
                onChange={set("message")}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm font-bold text-obsidian transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Sending..." : "Request Callback"}
            </button>
            <p className="mt-4 text-center font-mono text-[10px] text-slate-600">
              Your details are never shared. Enquiries go straight to the senior desk.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
